import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Image, 
  Settings, 
  MessageSquare, 
  TrendingUp,
  Users,
  Calendar,
  Star
} from 'lucide-react';
import apiService from '../services/api';
import { DashboardStats, ContactStats } from '../types';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [contactStats, setContactStats] = useState<ContactStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch basic stats
        const [services, images, pages, contacts] = await Promise.all([
          apiService.getServices(),
          apiService.getGalleryImages(1, 1), // Just get count
          apiService.getPages(),
          apiService.getContactSubmissions(1, 1) // Just get count
        ]);

        const dashboardStats: DashboardStats = {
          totalServices: services.length,
          totalImages: images.pagination.total_items,
          totalPages: pages.length,
          newContacts: 0, // Will be updated with contact stats
          totalContacts: contacts.pagination.total_items
        };

        setStats(dashboardStats);

        // Fetch contact stats
        const contactStatsData = await apiService.getContactStats();
        setContactStats(contactStatsData);
        setStats(prev => prev ? { ...prev, newContacts: contactStatsData.new } : null);

      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statCards = [
    {
      name: 'Total Services',
      value: stats?.totalServices || 0,
      icon: Settings,
      color: 'bg-blue-500',
      change: '+12%',
      changeType: 'positive'
    },
    {
      name: 'Gallery Images',
      value: stats?.totalImages || 0,
      icon: Image,
      color: 'bg-green-500',
      change: '+8%',
      changeType: 'positive'
    },
    {
      name: 'Pages',
      value: stats?.totalPages || 0,
      icon: FileText,
      color: 'bg-purple-500',
      change: '+5%',
      changeType: 'positive'
    },
    {
      name: 'New Contacts',
      value: stats?.newContacts || 0,
      icon: MessageSquare,
      color: 'bg-orange-500',
      change: '+15%',
      changeType: 'positive'
    }
  ];

  const recentActivities = [
    { id: 1, action: 'New contact form submission', time: '2 minutes ago', type: 'contact' },
    { id: 2, action: 'Gallery image uploaded', time: '1 hour ago', type: 'gallery' },
    { id: 3, action: 'Service updated', time: '3 hours ago', type: 'service' },
    { id: 4, action: 'Page content modified', time: '1 day ago', type: 'page' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Welcome to Eden Spot Garden admin panel. Here's an overview of your website.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white overflow-hidden shadow rounded-lg"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`inline-flex items-center justify-center h-8 w-8 rounded-md ${stat.color} text-white`}>
                      <Icon className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {stat.name}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {stat.value}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-5 py-3">
                <div className="text-sm">
                  <div className="flex items-center">
                    <TrendingUp className="h-4 w-4 text-green-400" />
                    <span className="text-green-600 font-medium ml-1">
                      {stat.change}
                    </span>
                    <span className="text-gray-500 ml-2">from last month</span>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Contact Stats */}
      {contactStats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white shadow rounded-lg"
        >
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Contact Submissions Overview
            </h3>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{contactStats.total}</div>
                <div className="text-sm text-gray-500">Total</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">{contactStats.new}</div>
                <div className="text-sm text-gray-500">New</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">{contactStats.contacted}</div>
                <div className="text-sm text-gray-500">Contacted</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{contactStats.booked}</div>
                <div className="text-sm text-gray-500">Booked</div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Recent Activity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white shadow rounded-lg"
      >
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Recent Activity
          </h3>
          <div className="flow-root">
            <ul className="-mb-8">
              {recentActivities.map((activity, activityIdx) => (
                <li key={activity.id}>
                  <div className="relative pb-8">
                    {activityIdx !== recentActivities.length - 1 ? (
                      <span
                        className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                        aria-hidden="true"
                      />
                    ) : null}
                    <div className="relative flex space-x-3">
                      <div>
                        <span className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center ring-8 ring-white">
                          {activity.type === 'contact' && <MessageSquare className="h-4 w-4 text-white" />}
                          {activity.type === 'gallery' && <Image className="h-4 w-4 text-white" />}
                          {activity.type === 'service' && <Settings className="h-4 w-4 text-white" />}
                          {activity.type === 'page' && <FileText className="h-4 w-4 text-white" />}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                        <div>
                          <p className="text-sm text-gray-500">
                            {activity.action}
                          </p>
                        </div>
                        <div className="text-right text-sm whitespace-nowrap text-gray-500">
                          <time>{activity.time}</time>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white shadow rounded-lg"
      >
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Quick Actions
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <button 
              onClick={() => navigate('/admin/gallery')}
              className="flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200"
            >
              <Image className="h-4 w-4 mr-2" />
              Upload Image
            </button>
            <button 
              onClick={() => navigate('/admin/services')}
              className="flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
            >
              <Settings className="h-4 w-4 mr-2" />
              Add Service
            </button>
            <button 
              onClick={() => navigate('/admin/pages')}
              className="flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 transition-colors duration-200"
            >
              <FileText className="h-4 w-4 mr-2" />
              Create Page
            </button>
            <button 
              onClick={() => navigate('/admin/contact')}
              className="flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 transition-colors duration-200"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              View Contacts
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard; 